// Cliente PÚBLICO (sin autenticación) para la web de marketing.
//
// La web pública necesita leer el catálogo de planes REAL de la base de datos
// (precios, moneda y límites configurados), pero sin sesión. El backend expone
// `GET /public/plans` SIN autorizador para este fin. Aquí hacemos un `fetch`
// directo a `config.apiUrl` sin token.

import { config } from '../config.js';

/** Límite de un plan por métrica (null en maxCount = ilimitado). */
export interface PublicPlanLimit {
  metric: 'catalog_records' | 'enrichment_ops' | string;
  period: 'total' | 'monthly' | string;
  maxCount: number | null;
}

/** Plan público tal como lo devuelve `GET /public/plans`. */
export interface PublicPlan {
  code: string;
  name: string;
  description: string | null;
  price_cents_monthly: number;
  price_cents_yearly: number;
  currency: string;
  billing_period: string;
  sort_order: number;
  is_default: boolean;
  limits: PublicPlanLimit[];
}

/** Carga los planes activos desde el endpoint público. */
export async function fetchPublicPlans(signal?: AbortSignal): Promise<PublicPlan[]> {
  const res = await fetch(`${config.apiUrl}/public/plans`, {
    method: 'GET',
    headers: { accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    throw new Error(`No se pudieron cargar los planes (HTTP ${res.status}).`);
  }
  const data = (await res.json()) as { items?: PublicPlan[] };
  return Array.isArray(data.items) ? data.items : [];
}

/** Sección de un documento legal (encabezado + párrafo). */
export interface LegalSection {
  h: string;
  p: string;
}

/** Documento legal vigente tal como lo devuelve `GET /public/legal`. */
export interface PublicLegalDocument {
  kind: 'terms' | 'privacy' | 'legal_notice';
  slug: string;
  version_id: string;
  version: string;
  title: string;
  summary: string | null;
  locale: string;
  sections: LegalSection[];
  content_hash: string;
  effective_at: string | null;
  published_at: string | null;
}

/** Carga TODOS los documentos legales vigentes desde el endpoint público. */
export async function fetchLegalDocuments(signal?: AbortSignal): Promise<PublicLegalDocument[]> {
  const res = await fetch(`${config.apiUrl}/public/legal`, {
    method: 'GET',
    headers: { accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    throw new Error(`No se pudieron cargar los documentos legales (HTTP ${res.status}).`);
  }
  const data = (await res.json()) as { items?: PublicLegalDocument[] };
  return Array.isArray(data.items) ? data.items : [];
}

/** Carga un documento legal vigente por su slug (o `null` si no existe). */
export async function fetchLegalDocument(
  slug: string,
  signal?: AbortSignal,
): Promise<PublicLegalDocument | null> {
  const res = await fetch(`${config.apiUrl}/public/legal/${encodeURIComponent(slug)}`, {
    method: 'GET',
    headers: { accept: 'application/json' },
    signal,
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`No se pudo cargar el documento legal (HTTP ${res.status}).`);
  }
  return (await res.json()) as PublicLegalDocument;
}

/** Datos para registrar el consentimiento legal en el alta. */
export interface RecordConsentInput {
  email: string;
  /** Versiones EXACTAS aceptadas (ids de `legal_document_version`). */
  versionIds: string[];
  /** `sub` de Cognito si ya se conoce (opcional). */
  cognitoSub?: string | null;
  /** Método de aceptación (por defecto 'signup_checkbox'). */
  method?: string;
  /** Idioma mostrado al aceptar. */
  locale?: string;
}

/** Resultado de registrar el consentimiento. */
export interface RecordConsentResult {
  recorded: number;
  consents: Array<{ id: string; kind: string; version: string }>;
  /**
   * `true` SOLO cuando ESTA llamada reclamó el aviso de alta (cuenta NUEVA);
   * el backend lo expone desde el reclamo atómico one-shot de
   * `registration_notice`. Opcional: un backend anterior a RA-01 no envía el
   * campo (llega `undefined`), y el cliente lo trata como `false` — se prefiere
   * NO medir a medir de más. Se usa para emitir `sign_up` exactamente una vez
   * por alta en el camino federado (ver AuthCallbackView).
   */
  registration?: boolean;
}

/**
 * Registra la APROBACIÓN del usuario de las versiones vigentes de los documentos
 * legales (log transaccional en el backend). IP y user-agent los deriva el
 * servidor. Es best-effort desde la UI: si falla, no debe impedir el alta, pero
 * sí se debe reintentar/registrar (ver RegisterView).
 */
export async function recordLegalConsent(
  input: RecordConsentInput,
  signal?: AbortSignal,
): Promise<RecordConsentResult> {
  const res = await fetch(`${config.apiUrl}/public/legal/consent`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({
      email: input.email,
      version_ids: input.versionIds,
      cognito_sub: input.cognitoSub ?? undefined,
      method: input.method ?? 'signup_checkbox',
      locale: input.locale ?? 'es',
    }),
    signal,
  });
  if (!res.ok) {
    throw new Error(`No se pudo registrar el consentimiento (HTTP ${res.status}).`);
  }
  return (await res.json()) as RecordConsentResult;
}

/** Una decisión de consentimiento granular (categoría + estado). */
export interface ConsentPreferenceInput {
  purpose: 'analytics' | 'session_replay' | 'marketing';
  granted: boolean;
}

/**
 * Registra en el backend (log append-only) las preferencias del banner de
 * cookies, para tener PRUEBA del lado servidor. Público: puede llamarse sin
 * sesión (identidad por email opcional). IP y user-agent los deriva el servidor.
 * Best-effort desde la UI: un fallo de red no debe romper la experiencia (la
 * decisión ya rige localmente y gobierna la carga de scripts).
 */
export async function recordConsentPreferences(
  preferences: ConsentPreferenceInput[],
  opts: { email?: string | null; policyVersion?: string; locale?: string } = {},
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${config.apiUrl}/public/consent-preferences`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({
      preferences,
      email: opts.email ?? undefined,
      policy_version: opts.policyVersion ?? undefined,
      locale: opts.locale ?? undefined,
      source: 'cookie_banner',
    }),
    signal,
  });
  if (!res.ok) {
    throw new Error(`No se pudo registrar el consentimiento de cookies (HTTP ${res.status}).`);
  }
}
